import {
  Shield,
  Cloud,
  Network,
  BarChart3,
  Bot,
  Phone,
  Headphones,
  type LucideIcon,
} from 'lucide-react'

export interface Assessment {
  id: string
  title: string
  shortTitle: string
  tagline: string
  description: string
  longDescription: string
  icon: LucideIcon
  color: string
  colorClass: string
  bgGradient: string
  href: string
  externalUrl?: string
  externalCtaText?: string
  benefits: string[]
  processSteps: {
    title: string
    description: string
  }[]
  deliverables: string[]
  features: {
    title: string
    description: string
    icon: LucideIcon
  }[]
}

export const assessments: Assessment[] = [
  {
    id: 'cybersecurity',
    title: 'Cyber Security Assessment',
    shortTitle: 'Cybersecurity',
    tagline: 'Protect your organization from evolving threats',
    description: 'Comprehensive security evaluation using the Cyber Pyramid framework to identify vulnerabilities and strengthen your security posture.',
    longDescription: 'Our Cyber Security Assessment provides a thorough evaluation of your organization\'s security infrastructure, policies, and practices. Using the industry-leading Cyber Pyramid framework, we identify vulnerabilities across all layers of your security stack—from endpoint protection to network security to user awareness. This assessment is designed specifically for financial institutions facing increasing regulatory scrutiny and sophisticated cyber threats.',
    icon: Shield,
    color: 'red',
    colorClass: 'text-red-500',
    bgGradient: 'from-red-500/20 to-red-600/10',
    href: '/assessments/cybersecurity',
    externalUrl: 'https://cyber.ucadvisors.com/',
    externalCtaText: 'Start Your Assessment',
    benefits: [
      'Identify critical security gaps before attackers do',
      'Meet NCUA, FFIEC, and other regulatory compliance requirements',
      'Reduce risk of data breaches and ransomware attacks',
      'Prioritized remediation roadmap based on risk level',
      'Executive-ready reporting for board presentations',
    ],
    processSteps: [
      {
        title: 'Discovery & Scoping',
        description: 'We start by understanding your current security infrastructure, policies, and business objectives to tailor the assessment to your needs.',
      },
      {
        title: 'Technical Assessment',
        description: 'Our team conducts vulnerability scanning, penetration testing, and configuration reviews across your entire environment.',
      },
      {
        title: 'Policy & Process Review',
        description: 'We evaluate your security policies, incident response procedures, and employee security awareness training programs.',
      },
      {
        title: 'Risk Analysis & Reporting',
        description: 'Findings are analyzed, prioritized by risk, and compiled into actionable recommendations with a clear remediation roadmap.',
      },
    ],
    deliverables: [
      'Executive Summary Report',
      'Detailed Technical Findings',
      'Risk-Prioritized Remediation Roadmap',
      'Compliance Gap Analysis',
      'Security Architecture Recommendations',
    ],
    features: [
      {
        title: 'Vulnerability Assessment',
        description: 'Comprehensive scanning of networks, applications, and endpoints to identify security weaknesses.',
        icon: Shield,
      },
      {
        title: 'Penetration Testing',
        description: 'Simulated attacks to test your defenses and identify exploitable vulnerabilities.',
        icon: Shield,
      },
      {
        title: 'Compliance Review',
        description: 'Evaluation against NCUA, FFIEC, GLBA, and other relevant regulatory frameworks.',
        icon: Shield,
      },
      {
        title: 'Security Awareness Audit',
        description: 'Assessment of employee security training and phishing susceptibility.',
        icon: Shield,
      },
    ],
  },
  {
    id: 'cloud',
    title: 'Cloud Assessment',
    shortTitle: 'Cloud',
    tagline: 'Optimize your cloud strategy and migration',
    description: 'Strategic cloud readiness and optimization assessment to help you migrate, modernize, and maximize your cloud investments.',
    longDescription: 'Our Cloud Assessment helps organizations evaluate their current infrastructure and develop a strategic roadmap for cloud adoption or optimization. Whether you\'re considering a migration from on-premises systems, looking to optimize existing cloud deployments, or planning a hybrid approach, we provide the insights needed to make informed decisions that align with your business goals and budget.',
    icon: Cloud,
    color: 'sky',
    colorClass: 'text-sky-500',
    bgGradient: 'from-sky-500/20 to-sky-600/10',
    href: '/assessments/cloud',
    benefits: [
      'Clear cloud migration strategy aligned with business goals',
      'Cost optimization for existing cloud deployments',
      'Improved security and compliance in cloud environments',
      'Reduced technical debt and modernized applications',
      'Enhanced scalability and business continuity',
    ],
    processSteps: [
      {
        title: 'Current State Analysis',
        description: 'We inventory and analyze your existing infrastructure, applications, and data to understand migration complexity.',
      },
      {
        title: 'Cloud Readiness Evaluation',
        description: 'Assessment of your organization\'s readiness across technology, people, and processes for cloud adoption.',
      },
      {
        title: 'Strategy Development',
        description: 'We develop a tailored cloud strategy including provider recommendations, migration approach, and timeline.',
      },
      {
        title: 'Roadmap & Business Case',
        description: 'Detailed implementation roadmap with cost projections, ROI analysis, and risk mitigation strategies.',
      },
    ],
    deliverables: [
      'Cloud Readiness Assessment Report',
      'Application Portfolio Analysis',
      'Migration Strategy & Roadmap',
      'TCO/ROI Analysis',
      'Security & Compliance Recommendations',
    ],
    features: [
      {
        title: 'Infrastructure Analysis',
        description: 'Complete inventory and analysis of servers, storage, networking, and applications.',
        icon: Cloud,
      },
      {
        title: 'Cost Modeling',
        description: 'Detailed TCO comparison between on-premises, cloud, and hybrid scenarios.',
        icon: Cloud,
      },
      {
        title: 'Migration Planning',
        description: 'Application-by-application migration strategy with dependency mapping.',
        icon: Cloud,
      },
      {
        title: 'Security Architecture',
        description: 'Cloud security design ensuring compliance and data protection.',
        icon: Cloud,
      },
    ],
  },
  {
    id: 'network',
    title: 'Network Assessment',
    shortTitle: 'Network',
    tagline: 'Optimize connectivity, performance, and reliability',
    description: 'In-depth network infrastructure assessment covering internet connectivity, SD-WAN readiness, and overall network performance optimization.',
    longDescription: 'Our Network Assessment provides a comprehensive evaluation of your organization\'s network infrastructure—from internet connectivity and ISP relationships to internal networking, branch connectivity, and SD-WAN readiness. We identify performance bottlenecks, single points of failure, and optimization opportunities to ensure your network supports your business needs reliably and cost-effectively.',
    icon: Network,
    color: 'orange',
    colorClass: 'text-orange-500',
    bgGradient: 'from-orange-500/20 to-orange-600/10',
    href: '/assessments/network',
    benefits: [
      'Improved network performance and user experience',
      'Reduced connectivity costs through vendor optimization',
      'Enhanced reliability with redundancy planning',
      'SD-WAN readiness for modern distributed operations',
      'Better visibility into network utilization and trends',
    ],
    processSteps: [
      {
        title: 'Network Discovery',
        description: 'Complete mapping of your network topology, including all locations, connections, and devices.',
      },
      {
        title: 'Performance Analysis',
        description: 'Assessment of bandwidth utilization, latency, packet loss, and application performance across your network.',
      },
      {
        title: 'Vendor & Cost Review',
        description: 'Analysis of current ISP contracts, pricing, and service levels against market alternatives.',
      },
      {
        title: 'Optimization Recommendations',
        description: 'Detailed recommendations for improving performance, reliability, and cost-efficiency.',
      },
    ],
    deliverables: [
      'Network Architecture Documentation',
      'Performance Benchmark Report',
      'Vendor Comparison Analysis',
      'SD-WAN Readiness Assessment',
      'Optimization Roadmap',
    ],
    features: [
      {
        title: 'Bandwidth Analysis',
        description: 'Detailed analysis of current and projected bandwidth requirements.',
        icon: Network,
      },
      {
        title: 'Latency Optimization',
        description: 'Identification and resolution of latency issues affecting critical applications.',
        icon: Network,
      },
      {
        title: 'Redundancy Planning',
        description: 'Design of failover and redundancy strategies for business continuity.',
        icon: Network,
      },
      {
        title: 'SD-WAN Evaluation',
        description: 'Assessment of SD-WAN solutions for improved performance and cost savings.',
        icon: Network,
      },
    ],
  },
  {
    id: 'data-analytics',
    title: 'Data Analytics Strategy Assessment',
    shortTitle: 'Data Analytics',
    tagline: 'Transform data into actionable business insights',
    description: 'Strategic assessment of your data analytics capabilities to help you leverage data for better decision-making and competitive advantage.',
    longDescription: 'Our Data Analytics Strategy Assessment helps organizations understand their current data landscape and develop a roadmap for becoming more data-driven. We evaluate your data sources, quality, governance, and analytics capabilities to identify opportunities for improvement. Whether you\'re just starting your analytics journey or looking to advance your capabilities, we provide actionable recommendations tailored to your business objectives.',
    icon: BarChart3,
    color: 'violet',
    colorClass: 'text-violet-500',
    bgGradient: 'from-violet-500/20 to-violet-600/10',
    href: '/assessments/data-analytics',
    benefits: [
      'Clear data strategy aligned with business objectives',
      'Improved data quality and governance',
      'Enhanced reporting and analytics capabilities',
      'Better decision-making through data insights',
      'Foundation for advanced analytics and AI initiatives',
    ],
    processSteps: [
      {
        title: 'Data Landscape Discovery',
        description: 'Inventory of all data sources, systems, and current analytics tools across your organization.',
      },
      {
        title: 'Maturity Assessment',
        description: 'Evaluation of your organization\'s analytics maturity across technology, people, and processes.',
      },
      {
        title: 'Gap Analysis',
        description: 'Identification of gaps between current state and desired analytics capabilities.',
      },
      {
        title: 'Strategy & Roadmap',
        description: 'Development of a prioritized roadmap with quick wins and long-term strategic initiatives.',
      },
    ],
    deliverables: [
      'Data Landscape Documentation',
      'Analytics Maturity Assessment',
      'Data Governance Recommendations',
      'Technology Stack Evaluation',
      'Analytics Strategy Roadmap',
    ],
    features: [
      {
        title: 'Data Source Inventory',
        description: 'Complete mapping of all data sources and their relationships.',
        icon: BarChart3,
      },
      {
        title: 'Data Quality Assessment',
        description: 'Evaluation of data accuracy, completeness, and consistency.',
        icon: BarChart3,
      },
      {
        title: 'Analytics Tool Review',
        description: 'Assessment of current BI and analytics tools against business needs.',
        icon: BarChart3,
      },
      {
        title: 'Governance Framework',
        description: 'Recommendations for data governance policies and procedures.',
        icon: BarChart3,
      },
    ],
  },
  {
    id: 'ai',
    title: 'AI Assessment',
    shortTitle: 'AI',
    tagline: 'Unlock the potential of artificial intelligence',
    description: 'Strategic AI readiness assessment to identify high-impact opportunities for AI and machine learning in your organization.',
    longDescription: 'Our AI Assessment helps organizations navigate the rapidly evolving landscape of artificial intelligence and machine learning. We evaluate your organization\'s readiness for AI adoption, identify high-value use cases specific to your industry, and develop a practical roadmap for implementation. From intelligent automation to predictive analytics to conversational AI, we help you cut through the hype and focus on initiatives that deliver real business value.',
    icon: Bot,
    color: 'fuchsia',
    colorClass: 'text-fuchsia-500',
    bgGradient: 'from-fuchsia-500/20 to-fuchsia-600/10',
    href: '/assessments/ai',
    benefits: [
      'Identify high-ROI AI use cases for your organization',
      'Understand AI readiness across data, technology, and skills',
      'Avoid common AI implementation pitfalls',
      'Develop ethical AI guidelines and governance',
      'Practical roadmap from quick wins to strategic initiatives',
    ],
    processSteps: [
      {
        title: 'AI Opportunity Discovery',
        description: 'Workshops with key stakeholders to identify potential AI use cases across your organization.',
      },
      {
        title: 'Readiness Assessment',
        description: 'Evaluation of your data, technology infrastructure, and team capabilities for AI initiatives.',
      },
      {
        title: 'Use Case Prioritization',
        description: 'Analysis and prioritization of AI opportunities based on value, feasibility, and risk.',
      },
      {
        title: 'Implementation Roadmap',
        description: 'Development of a phased AI roadmap with resource requirements and success metrics.',
      },
    ],
    deliverables: [
      'AI Use Case Catalog',
      'AI Readiness Assessment',
      'Prioritized Opportunity Matrix',
      'AI Governance Framework',
      'Implementation Roadmap',
    ],
    features: [
      {
        title: 'Use Case Identification',
        description: 'Discovery of AI opportunities across operations, customer service, and decision-making.',
        icon: Bot,
      },
      {
        title: 'Data Readiness',
        description: 'Assessment of data availability and quality for machine learning initiatives.',
        icon: Bot,
      },
      {
        title: 'Technology Evaluation',
        description: 'Review of AI platforms and tools aligned with your needs and capabilities.',
        icon: Bot,
      },
      {
        title: 'Skills Assessment',
        description: 'Evaluation of team capabilities and training needs for AI adoption.',
        icon: Bot,
      },
    ],
  },
  {
    id: 'ucaas',
    title: 'UCaaS Assessment',
    shortTitle: 'UCaaS',
    tagline: 'Modernize your unified communications',
    description: 'Comprehensive assessment of your unified communications needs to optimize voice, video, messaging, and collaboration tools.',
    longDescription: 'Our UCaaS (Unified Communications as a Service) Assessment helps organizations evaluate their current communications infrastructure and develop a strategy for modernization. Whether you\'re dealing with aging PBX systems, managing multiple disconnected tools, or looking to enable remote work, we provide a thorough analysis of your needs and a clear roadmap to a unified, cloud-based communications platform that enhances productivity and reduces costs.',
    icon: Phone,
    color: 'teal',
    colorClass: 'text-teal-500',
    bgGradient: 'from-teal-500/20 to-teal-600/10',
    href: '/assessments/ucaas',
    benefits: [
      'Consolidated communications with reduced complexity',
      'Lower total cost of ownership vs. legacy systems',
      'Enhanced collaboration and productivity',
      'Support for remote and hybrid work models',
      'Provider-agnostic recommendations for best fit',
    ],
    processSteps: [
      {
        title: 'Current State Assessment',
        description: 'Inventory of existing phone systems, collaboration tools, and communication workflows.',
      },
      {
        title: 'Requirements Gathering',
        description: 'Detailed discovery of communication needs across all departments and locations.',
      },
      {
        title: 'Vendor Evaluation',
        description: 'Provider-agnostic comparison of UCaaS solutions based on your specific requirements.',
      },
      {
        title: 'Migration Planning',
        description: 'Development of a migration strategy including number porting, training, and cutover planning.',
      },
    ],
    deliverables: [
      'Communications Infrastructure Assessment',
      'Feature Requirements Matrix',
      'UCaaS Vendor Comparison',
      'Cost Analysis & ROI Model',
      'Migration Roadmap',
    ],
    features: [
      {
        title: 'Voice Analysis',
        description: 'Evaluation of current phone systems, call volumes, and feature utilization.',
        icon: Phone,
      },
      {
        title: 'Collaboration Audit',
        description: 'Assessment of video conferencing, messaging, and team collaboration needs.',
        icon: Phone,
      },
      {
        title: 'Integration Review',
        description: 'Analysis of required integrations with CRM, helpdesk, and business applications.',
        icon: Phone,
      },
      {
        title: 'Remote Work Enablement',
        description: 'Evaluation of mobility and remote work communication requirements.',
        icon: Phone,
      },
    ],
  },
  {
    id: 'contact-center',
    title: 'Contact Center Assessment',
    shortTitle: 'Contact Center',
    tagline: 'Transform your member and customer experience',
    description: 'Strategic contact center assessment to optimize your member service operations with modern CCaaS solutions.',
    longDescription: 'Our Contact Center Assessment provides a comprehensive evaluation of your member or customer service operations. We analyze your current contact center technology, processes, and performance metrics to identify opportunities for improvement. Whether you\'re running a legacy on-premises system or an existing cloud solution, we help you develop a strategy for delivering exceptional experiences across voice, chat, email, and social channels while improving operational efficiency.',
    icon: Headphones,
    color: 'emerald',
    colorClass: 'text-emerald-500',
    bgGradient: 'from-emerald-500/20 to-emerald-600/10',
    href: '/assessments/contact-center',
    benefits: [
      'Improved member/customer satisfaction scores',
      'Reduced average handle time and wait times',
      'Omnichannel capabilities for modern service expectations',
      'Enhanced agent productivity and satisfaction',
      'Better visibility through advanced analytics',
    ],
    processSteps: [
      {
        title: 'Operations Review',
        description: 'Analysis of current contact center operations, volumes, and performance metrics.',
      },
      {
        title: 'Technology Assessment',
        description: 'Evaluation of existing contact center technology, integrations, and capabilities.',
      },
      {
        title: 'Experience Mapping',
        description: 'Documentation of member/customer journeys across all service channels.',
      },
      {
        title: 'Optimization Strategy',
        description: 'Development of recommendations for technology, process, and people improvements.',
      },
    ],
    deliverables: [
      'Contact Center Operations Report',
      'Technology Gap Analysis',
      'Customer Journey Maps',
      'CCaaS Vendor Comparison',
      'Optimization Roadmap',
    ],
    features: [
      {
        title: 'Channel Analysis',
        description: 'Assessment of voice, chat, email, and digital channel performance.',
        icon: Headphones,
      },
      {
        title: 'Workforce Optimization',
        description: 'Evaluation of scheduling, forecasting, and quality management practices.',
        icon: Headphones,
      },
      {
        title: 'Self-Service Review',
        description: 'Assessment of IVR, chatbot, and knowledge base effectiveness.',
        icon: Headphones,
      },
      {
        title: 'Analytics & Reporting',
        description: 'Review of metrics, dashboards, and analytics capabilities.',
        icon: Headphones,
      },
    ],
  },
]

export function getAssessmentById(id: string): Assessment | undefined {
  return assessments.find((a) => a.id === id)
}

export function getAssessmentBySlug(slug: string): Assessment | undefined {
  return assessments.find((a) => a.href === `/assessments/${slug}`)
}
